import React, { useEffect, useState } from "react";
import { driverApi } from "../../services/driverApi";

export default function DriverMessages({ driver }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { messages: m } = await driverApi.listMessages();
      setMessages(m || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function handleReply(e) {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSending(true);
    setError("");
    try {
      await driverApi.replyToMessage(selected._id, replyText.trim());
      setReplyText("");
      load();
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  }

  const unread = messages.filter((m) => {
    const rec = (m.recipients || []).find((r) => String(r.recipientId) === String(driver?._id));
    return rec && !rec.readAt;
  }).length;

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  if (selected) {
    const isAdminMsg = selected.senderType === "admin";
    return (
      <div>
        <button onClick={() => setSelected(null)} className="flex items-center gap-1.5 text-sm text-blue-600 hover:underline mb-4">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          Back to messages
        </button>

        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
          <h2 className="font-semibold text-gray-900 text-base mb-1">{selected.subject}</h2>
          <p className="text-xs text-gray-400 mb-4">
            From {isAdminMsg ? "Trans Voyage Office" : `${selected.senderName}`} · {new Date(selected.createdAt).toLocaleDateString()}
          </p>
          <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selected.body}</div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-3 text-sm">{error}</div>}

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-900 mb-3">Reply</p>
          <form onSubmit={handleReply} className="space-y-3">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              rows={4}
              placeholder="Type your reply…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <button type="submit" disabled={!replyText.trim() || sending}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg text-sm transition disabled:opacity-50">
              {sending ? "Sending…" : "Send Reply"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Messages</h1>
        {unread > 0 && <p className="text-sm text-blue-600 mt-0.5">{unread} unread message{unread !== 1 ? "s" : ""}</p>}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}

      {messages.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          No messages yet. The office will send you updates here.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {messages.map((m) => {
              const rec = (m.recipients || []).find((r) => String(r.recipientId) === String(driver?._id));
              const isRead = rec?.readAt;
              return (
                <li key={m._id}>
                  <button
                    onClick={() => { setSelected(m); if (!isRead) driverApi.markRead(m._id).catch(() => {}); }}
                    className="w-full text-left px-4 py-4 hover:bg-gray-50 transition flex items-start gap-3"
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isRead ? "bg-transparent" : "bg-blue-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm truncate ${isRead ? "text-gray-700" : "font-semibold text-gray-900"}`}>{m.subject}</p>
                        <p className="text-xs text-gray-400 flex-shrink-0">{new Date(m.createdAt).toLocaleDateString()}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {m.isGlobal ? "Sent to all drivers" : "Sent to you"} · {m.senderName || "Office"}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
